import { format } from 'date-fns';
import { atom, type Getter, type Setter, useAtomValue } from 'jotai';
import { atomWithStorage, useAtomCallback } from 'jotai/utils';
import { atomFamily } from 'jotai-family';
import { useCallback, useMemo } from 'react';
import type { InteractionType } from '@/common/types/interaction';
import {
  getEndDate,
  getStartDate,
  isValidDate,
} from '@/views/Home/components/FactsAndFigures/components/DatesFilter.helpers';
import {
  DATE_RANGE_OPTIONS,
  type DateInput,
} from '@/views/Home/components/FactsAndFigures/types';
import type { CoreSchema } from '$api-clients/generated';
import {
  type InteractionFilter,
  type Paging,
  TEAM_ID_FILTER,
  TEAM_MEMBER_ID_FILTER,
} from './filters.types';

type CoreServiceInteractionsQuery = NonNullable<
  CoreSchema.paths['/interactions']['get']['parameters']['query']
>;

export type CoreInteractionType = NonNullable<
  CoreServiceInteractionsQuery['type']
>;

export type InteractionTypeFilter = InteractionType | CoreInteractionType;

interface Options {
  archived?: boolean;
  parentInteractionId?: string;
}

export const getInteractionsFiltersKey = (
  type: InteractionTypeFilter | undefined,
  options: Options = {},
) => {
  const { archived = false, parentInteractionId } = options;

  return `${type}${archived ? '-archived' : ''}${parentInteractionId ? `-${parentInteractionId}` : ''}`;
};

/* PAGING */
export const DEFAULT_PAGE_SIZE = 10;

/** The list view opens on a full page of rows; the card grid pages itself. */
export const DEFAULT_LIST_PAGE_SIZE = 100;

export const interactionsPagingState = atomFamily(() =>
  atom<Paging | undefined>(undefined),
);

const resetPage = (stateKey: string, get: Getter, set: Setter) => {
  const paging = get(interactionsPagingState(stateKey));
  if (paging?.page && paging.page > 1) {
    set(interactionsPagingState(stateKey), (prevPaging) => ({
      page: 1,
      totalPages: prevPaging?.totalPages ?? 0,
      totalItems: prevPaging?.totalItems ?? 0,
      perPage: prevPaging?.perPage ?? 0,
    }));
  }
};

export const defaultSortValue = 'createdAt:desc';

/* SORTING */
export const interactionsSortState = atomFamily((stateKey: string) =>
  atomWithStorage(`${stateKey}-sort`, defaultSortValue, undefined, {
    getOnInit: true,
  }),
);

export const useInteractionsSortState = (
  stateKey: string,
  fallbackValue: string = defaultSortValue,
) => {
  const state = useAtomValue(interactionsSortState(stateKey));

  const setState = useAtomCallback(
    useCallback(
      (get, set, value: string | undefined) => {
        const newValue = value ?? fallbackValue;
        if (get(interactionsSortState(stateKey)) !== newValue) {
          resetPage(stateKey, get, set);
        }
        set(interactionsSortState(stateKey), newValue);
      },
      [stateKey, fallbackValue],
    ),
  );

  return [state, setState] as const;
};

/* SEARCHING */
export const interactionsSearchState = atomFamily(() =>
  atom<string | undefined>(undefined),
);

export function useSetInteractionsSearchState(stateKey: string) {
  return useAtomCallback((get, set, search: string) => {
    const previousSearch = get(interactionsSearchState(stateKey));

    // Reset to page 1 if search has changed
    if (previousSearch !== search) {
      resetPage(stateKey, get, set);
    }
    set(interactionsSearchState(stateKey), search);
  });
}

export function useResetEphemeralInteractionsFiltersState(stateKey: string) {
  return useAtomCallback((get, set) => {
    resetPage(stateKey, get, set);
    set(interactionsSearchState(stateKey), '');
  });
}

/* FILTERING - maintain a total list */
export const interactionsFiltersState = atomFamily((stateKey: string) =>
  atomWithStorage<InteractionFilter[]>(`${stateKey}-filters`, [], undefined, {
    getOnInit: true,
  }),
);

export const interactionsFiltersCountState = atomFamily((stateKey: string) =>
  atom((get) => get(interactionsFiltersState(stateKey)).length),
);

/* FILTERING - Created Date */
type DateCreatedFilter = {
  dateRange: DATE_RANGE_OPTIONS;
  endDate: DateInput;
  startDate: DateInput;
};

export const interactionsDateCreatedFilterState = atomFamily(
  (stateKey: string) =>
    atomWithStorage<DateCreatedFilter>(
      `${stateKey}-date-created`,
      {
        dateRange: DATE_RANGE_OPTIONS.DEFAULT,
        endDate: null,
        startDate: null,
      },
      undefined,
      { getOnInit: true },
    ),
);

/**
 * Returns the interactions filters with stale relative date ranges
 * (LTM, LTD, YTD) recomputed to fresh dates.
 */
export function useResolvedInteractionsFilters(
  stateKey: string,
): InteractionFilter[] {
  const filters = useAtomValue(interactionsFiltersState(stateKey));
  const { dateRange, startDate, endDate } = useAtomValue(
    interactionsDateCreatedFilterState(stateKey),
  );

  return useMemo(() => {
    if (
      dateRange === DATE_RANGE_OPTIONS.DEFAULT ||
      dateRange === DATE_RANGE_OPTIONS.CUSTOM
    ) {
      return filters;
    }

    if (!filters.some((f) => f.label.startsWith('createdAt'))) {
      return filters;
    }

    const freshStart = getStartDate({
      dateRange,
      customStartDateValue: startDate,
    });
    const freshEnd = getEndDate({ dateRange, customEndDateValue: endDate });

    if (!isValidDate(freshStart) || !isValidDate(freshEnd)) {
      return filters;
    }

    const formattedStart = format(freshStart, 'yyyy-MM-dd');
    const formattedEnd = format(freshEnd, 'yyyy-MM-dd');

    return filters.map((f) =>
      f.label.startsWith('createdAt')
        ? {
            ...f,
            value: `createdAt:[${formattedStart} TO ${formattedEnd}]`,
          }
        : f,
    );
  }, [filters, dateRange, startDate, endDate]);
}

export const useSetDateCreatedFilterState = (stateKey: string) =>
  useAtomCallback((get, set, newFilter: Partial<DateCreatedFilter>) => {
    resetPage(stateKey, get, set);

    const currentFilter = get(interactionsDateCreatedFilterState(stateKey));
    const filter = {
      ...currentFilter,
      ...newFilter,
    };

    const startDate = getStartDate({
      dateRange: filter.dateRange,
      customStartDateValue: filter.startDate,
    });

    const endDate = getEndDate({
      dateRange: filter.dateRange,
      customEndDateValue: filter.endDate,
    });

    set(interactionsFiltersState(stateKey), (prev) => {
      const filtersWithoutDate = prev.filter(
        (f) => !f.label.startsWith('createdAt'),
      );

      if (
        filter.dateRange === DATE_RANGE_OPTIONS.DEFAULT ||
        !isValidDate(startDate) ||
        !isValidDate(endDate)
      ) {
        return filtersWithoutDate;
      }

      const formattedStartDate = format(startDate, 'yyyy-MM-dd');
      const formattedEndDate = format(endDate, 'yyyy-MM-dd');

      return [
        {
          label: `createdAt:${filter.dateRange}`,
          value: `createdAt:[${formattedStartDate} TO ${formattedEndDate}]`,
        },
        ...filtersWithoutDate,
      ];
    });

    set(interactionsDateCreatedFilterState(stateKey), filter);
  });

export const useClearDateFilter = (stateKey: string) => {
  const setDateCreatedState = useSetDateCreatedFilterState(stateKey);
  return useCallback(() => {
    setDateCreatedState({
      dateRange: DATE_RANGE_OPTIONS.DEFAULT,
      endDate: null,
      startDate: null,
    });
  }, [setDateCreatedState]);
};

/**
 * Clears all filters including persisted filters (in localStorage)
 * Also clears ephemeral state (search and paging) and resets sort to default
 * Used when deep linking with clearFilters=true parameter
 */
export function useClearAllInteractionsFilters(stateKey: string) {
  const clearDateFilters = useClearDateFilter(stateKey);
  const resetEphemeralFilters =
    useResetEphemeralInteractionsFiltersState(stateKey);

  return useAtomCallback((_get, set) => {
    // Clear all main filters
    set(interactionsFiltersState(stateKey), []);
    // Clear date filters
    clearDateFilters();
    // Reset sort to default
    set(interactionsSortState(stateKey), defaultSortValue);
    // Reset ephemeral state (search and paging)
    resetEphemeralFilters();
  });
}

/**
 * Removes a filter from the main filter list.
 * @param type InteractionType
 * @param archived boolean
 * @returns void
 */
export function useRemoveInteractionsFilterState(stateKey: string) {
  const clearDateFilters = useClearDateFilter(stateKey);

  return useAtomCallback((get, set, filter: InteractionFilter) => {
    resetPage(stateKey, get, set);
    set(interactionsFiltersState(stateKey), (filters) =>
      filters.filter((f) => f.value !== filter.value),
    );

    // Handle removal of date filters
    if (filter.label.startsWith('createdAt')) {
      clearDateFilters();
    }
  });
}

/**
 * Creates a writable atom for a specific filter type that can be used to read, write, and reset filters
 */
const createFilterAtom = (filterKey: string) =>
  atomFamily((stateKey: string) =>
    atom(
      // Read function - get all filters for this type
      (get) => {
        const allFilters = get(interactionsFiltersState(stateKey));
        return allFilters.filter((filter) =>
          filter.value.startsWith(`${filterKey}:`),
        );
      },
      // Write function - update filters for this type
      (get, set, newFilters: InteractionFilter[]) => {
        resetPage(stateKey, get, set);
        set(interactionsFiltersState(stateKey), (prev) => {
          const nonMatchingFilters = prev.filter(
            (f) => !f.value.startsWith(`${filterKey}:`),
          );
          return [...nonMatchingFilters, ...newFilters];
        });
      },
    ),
  );

/**
 * Hook for toggling a filter in the main filter state
 */
export const useToggleFilter = (stateKey: string) =>
  useAtomCallback((get, set, filter: InteractionFilter) => {
    resetPage(stateKey, get, set);

    set(interactionsFiltersState(stateKey), (prev) => {
      if (prev.find((f) => f.value === filter.value)) {
        return prev.filter((f) => f.value !== filter.value);
      }
      return [filter, ...prev];
    });
  });

/* FILTERING - Representation Type */
export const interactionsRepresentationTypesState =
  createFilterAtom('representation');

/* FILTERING - Status */
export const interactionsStatusesState = createFilterAtom('status');

/* FILTERING - Referral Interaction Status */
const interactionsReferralInteractionStatusesState = createFilterAtom(
  'referralInteractionStatus',
);

/* FILTERING - Contact Type */
export const interactionsContactTypesState =
  createFilterAtom('contactEntityType');

/* FILTERING - Referral Client Type */
export const interactionsReferralClientTypesState =
  createFilterAtom('referralClientType');

/* FILTERING - Agreement Status */
export const interactionsAgreementStatusesState =
  createFilterAtom('agreementStatus');

/* FILTERING - Teams */
export const interactionsTeamsState = createFilterAtom(TEAM_ID_FILTER);

/* FILTERING - Agents */
export const interactionsAgentsState = createFilterAtom(TEAM_MEMBER_ID_FILTER);

/**
 * Returns the status filter atom for a given interaction type
 * @param type InteractionType
 * @returns filter atom
 */
export function getStatusFilterAtom(type: InteractionTypeFilter) {
  if (type === 'referral') {
    return interactionsReferralInteractionStatusesState;
  }
  if (type === 'clientGroup') {
    return interactionsAgreementStatusesState;
  }
  return interactionsStatusesState;
}

/* FILTERING - End Date */
type DateRange = {
  fromDate: DateInput;
  toDate: DateInput;
};

export const interactionsEndDateFilterState = atomFamily((stateKey: string) =>
  atomWithStorage<DateRange>(
    stateKey,
    {
      fromDate: null,
      toDate: null,
    },
    undefined,
    { getOnInit: true },
  ),
);

export const useSetEndDateFilterState = (stateKey: string) =>
  useAtomCallback((get, set, newFilter: Partial<DateRange>) => {
    resetPage(stateKey, get, set);

    const currentFilter = get(interactionsEndDateFilterState(stateKey));

    const filter = {
      ...currentFilter,
      ...newFilter,
    };

    set(interactionsFiltersState(stateKey), (prev) => {
      const filtersWithoutDate = prev.filter(
        (f) => !f.value.startsWith('endDate'),
      );

      const fromDate = filter.fromDate ? new Date(filter.fromDate) : null;
      const toDate = filter.toDate ? new Date(filter.toDate) : null;

      if (!isValidDate(fromDate) || !isValidDate(toDate)) {
        return filtersWithoutDate;
      }

      const formattedFromDate = format(fromDate, 'yyyy-MM-dd');
      const formattedToDate = format(toDate, 'yyyy-MM-dd');

      return [
        {
          // Label isn't used anywhere. Just here to satisfy types.
          label: 'End Date',
          value: `endDate:[${formattedFromDate} TO ${formattedToDate}]`,
        },
        ...filtersWithoutDate,
      ];
    });

    set(interactionsEndDateFilterState(stateKey), filter);
  });

export const useClearEndDateFilter = (stateKey: string) => {
  const setEndDateFilterState = useSetEndDateFilterState(stateKey);
  return useCallback(() => {
    setEndDateFilterState({
      fromDate: null,
      toDate: null,
    });
  }, [setEndDateFilterState]);
};
